/**
 * Event Modal Initialization for Non-Calendar Pages
 *
 * Contract:
 * - This script may be bundled on pages that do NOT have event titles or modal DOM.
 * - It must silently no-op when required DOM isn't present.
 */

import ModalManager from '../calendar/ui/ModalManager.js';
import ModalContentGenerator from '../calendar/ui/ModalContentGenerator.js';

class EventModalInitializer {
  constructor() {
    this.modalManager = null;
    this.contentGenerator = null;
    this.events = [];
    this._initialized = false;
  }

  init() {
    // Prevent double-init
    if (this._initialized) return true;

    // If there are no clickable event titles, this feature isn't used on this page
    const hasClickableTitles = document.querySelector('.event-title.clickable');
    if (!hasClickableTitles) return false;

    // Modal DOM must exist for this feature to work; if missing, silently no-op
    const overlay = document.getElementById('modal-overlay');
    const modalBody = document.getElementById('modal-body');
    if (!overlay || !modalBody) return false;

    try {
      // Initialize modal manager (lightweight setup for non-calendar pages)
      this.modalManager = new ModalManager();

      // Manual initialization without calendar-specific handlers
      this.modalManager.overlay = overlay;
      this.modalManager.modalBody = modalBody;
      this.modalManager.closeBtn = overlay.querySelector('.modal-close') || null;

      // Set up basic modal event listeners (close button, escape key, overlay click)
      this.setupModalEventListeners();

      // Mark as initialized
      this.modalManager.initialized = true;

      // Initialize content generator
      this.contentGenerator = new ModalContentGenerator();

      // Load event data from global scope or data attributes
      this.loadEventData();

      // Set up event listeners for clickable event titles
      this.setupEventListeners();

      // Re-attach handlers when pagination replaces the calendar list DOM
      document.addEventListener('calendarListUpdated', () => {
        this.setupEventListeners();
        this.updateDisplayedTitles();
      });

      this._initialized = true;
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize event modal system:', error);
      return false;
    }
  }

  setupModalEventListeners() {
    if (this.modalManager.closeBtn) {
      this.modalManager.closeBtn.addEventListener('click', () => this.modalManager.hide());
    }

    this.modalManager.overlay.addEventListener('click', e => {
      if (e.target === this.modalManager.overlay) {
        this.modalManager.hide();
      }
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.modalManager.isVisible && this.modalManager.isVisible()) {
        this.modalManager.hide();
      }
    });
  }

  loadEventData() {
    if (window.upcomingEvents && Array.isArray(window.upcomingEvents)) {
      this.events = window.upcomingEvents;
      return;
    }

    // Fallback: Parse events from DOM data attributes
    // (No logging; this is normal on some pages.)
    const eventElements = document.querySelectorAll('.event-item[data-event-index]');
    if (eventElements.length > 0) {
      // Events will be loaded from data attributes on click
    }
  }

  setupEventListeners() {
    const clickableEventTitles = document.querySelectorAll('.event-title.clickable');
    if (clickableEventTitles.length === 0) return;

    clickableEventTitles.forEach(titleElement => {
      titleElement.addEventListener('click', (e) => {
        this.handleEventClick(e, titleElement);
      });

      titleElement.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleEventClick(e, titleElement);
        }
      });

      // Add visual feedback
      titleElement.style.cursor = 'pointer';
    });
  }

  async handleEventClick(e, titleElement) {
    try {
      e.preventDefault();
      e.stopPropagation();

      const eventItem = titleElement.closest('.event-item');
      if (!eventItem) return;

      const event = this.getEventData(eventItem);
      if (!event) return;

      const content = await this.contentGenerator.generateModalContent(event, '');

      this.modalManager.show(content);
    } catch (error) {
      console.error('Error displaying event modal:', error);
    }
  }

  getEventData(eventItem) {
    const eventIndex = eventItem.dataset.eventIndex;
    const eventDate = eventItem.dataset.eventDate;

    if (this.events.length > 0) {
      const event = this.events.find((e, idx) =>
        String(e.display?.eventIndex) === eventIndex || idx.toString() === eventIndex
      );
      if (event) return event;

      const eventByDate = this.events.find(e => {
        const startDate = e.start?.dateTime || e.start?.date;
        return startDate === eventDate;
      });
      if (eventByDate) return eventByDate;
    }

    return this.extractEventDataFromDOM(eventItem);
  }

  /**
   * Replace displayed list titles with shortTitle from config where defined.
   * Falls back silently — full title remains if shortTitle is empty or config unavailable.
   */
  async updateDisplayedTitles() {
    try {
      const titleElements = document.querySelectorAll('.event-title.clickable');
      if (titleElements.length === 0) return;

      for (const el of titleElements) {
        const eventItem = el.closest('.event-item');
        if (!eventItem) continue;

        const event = this.getEventData(eventItem);
        if (!event) continue;

        const types = event.locData?.type
          ? (Array.isArray(event.locData.type) ? event.locData.type : [event.locData.type])
          : [];

        const shortTitle = await this.contentGenerator.getShortTitle(event.summary, types);
        if (shortTitle) {
          const strong = el.querySelector('strong');
          if (strong) strong.textContent = shortTitle;
        }
      }
    } catch (error) {
      // Gracefully degrade — full titles remain shown
    }
  }

  extractEventDataFromDOM(eventItem) {
    const title = eventItem.dataset.eventSummary || eventItem.querySelector('.event-title strong')?.textContent || 'Event';
    const timeRange = eventItem.querySelector('.event-time span')?.textContent || '';
    const eventDate = eventItem.dataset.eventDate;
    const eventId = eventItem.dataset.eventId || null;
    const recurringEventId = eventItem.dataset.eventRecurringId || null;
    const description = eventItem.dataset.eventDescription || '';

    let locData = null;
    try {
      const raw = eventItem.dataset.eventLocdata;
      if (raw) locData = JSON.parse(raw);
    } catch (e) { /* ignore */ }

    const typeElements = eventItem.querySelectorAll('.event-type');
    const types = Array.from(typeElements).map(el => ({
      name: el.textContent.trim().replace(',', ''),
      color: el.style.color || '#2c3e3e',
    }));

    const month = eventItem.querySelector('.calendar-icon .month')?.textContent || '';
    const day = eventItem.querySelector('.calendar-icon .day')?.textContent || '';

    return {
      id: eventId,
      recurringEventId,
      summary: title,
      start: { dateTime: eventDate, date: eventDate },
      description,
      descSum: description,
      locData,
      display: {
        timeRange,
        monthAbbrev: month,
        dayNum: day,
        types: {
          hasTypes: types.length > 0,
          typeList: types,
          displayText: types.map(t => t.name).join(', '),
        },
      },
    };
  }
}

// Auto-initialize when DOM is ready (silent no-op if not applicable)
function boot() {
  const initializer = new EventModalInitializer();
  initializer.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

export default EventModalInitializer;
